import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export interface ProjectFieldChange {
  field_name: string;
  old_value: string | null;
  new_value: string | null;
}

export interface ProjectVersionEntry {
  id: number;
  project_id: number;
  version: number;
  change_type: 'create' | 'update' | 'delete' | 'restore';
  change_summary: string;
  changed_by: number | null;
  changed_by_name?: string | null;
  changed_at: string;
  field_changes: ProjectFieldChange[];
}

@Component({
  selector: 'app-project-change-history',
  templateUrl: './project-change-history.component.html',
  styleUrl: './project-change-history.component.css',
})
export class ProjectChangeHistoryComponent implements OnChanges {
  @Input() projectId!: number | string;

  loading = false;
  restoring = false;
  error: string | null = null;
  entries: ProjectVersionEntry[] = [];
  total = 0;
  expandedVersion: number | null = null;

  constructor(
    private api: ApiService,
    private toastr: ToastrMsgService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    if (!this.projectId) return;
    this.loading = true;
    this.error = null;
    this.api.getProjectHistory(this.projectId, { limit: 100 }).subscribe({
      next: (res: any) => {
        this.entries = (res?.data ?? []).map((entry: ProjectVersionEntry) => ({
          ...entry,
          field_changes: this.getVisibleChanges(entry.field_changes || []),
        }));
        this.total = res?.total ?? this.entries.length;
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.error || err?.error?.message || 'Failed to load change history';
        this.loading = false;
      },
    });
  }

  toggleExpand(version: number): void {
    this.expandedVersion = this.expandedVersion === version ? null : version;
  }

  changeTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
      restore: 'Restored',
    };
    return labels[type] || type;
  }

  changeTypeClass(type: string): string {
    return `badge badge-${type}`;
  }

  fieldLabel(name: string): string {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private readonly dateFields = new Set([
    'timeline_start',
    'timeline_end',
    'start_date',
    'end_date',
    'handover_date',
    'public_access_date',
    'network_activation_date',
  ]);

  getVisibleChanges(changes: ProjectFieldChange[]): ProjectFieldChange[] {
    return changes.filter((fc) => {
      const oldVal = (fc.old_value ?? '').trim();
      const newVal = (fc.new_value ?? '').trim();
      if (!oldVal && !newVal) return false;
      if (this.dateFields.has(fc.field_name)) {
        return this.toCalendarDate(oldVal) !== this.toCalendarDate(newVal);
      }
      return oldVal !== newVal;
    });
  }

  private toCalendarDate(value: string): string {
    if (!value) return '';
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  displayValue(value: string | null, fieldName?: string): string {
    if (value === null || value === undefined || value === '') return '—';
    if (fieldName && this.dateFields.has(fieldName)) {
      const calendar = this.toCalendarDate(value);
      if (calendar) {
        const [y, m, d] = calendar.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
    }
    if (value.length > 120) return value.slice(0, 117) + '…';
    return value;
  }

  canRestore(entry: ProjectVersionEntry): boolean {
    return entry.change_type !== 'delete' && entry.version !== this.entries[0]?.version;
  }

  confirmRestore(entry: ProjectVersionEntry): void {
    if (!confirm(`Restore project to version ${entry.version}? Current data will be saved as a new version first.`)) {
      return;
    }
    this.restoring = true;
    this.api.restoreProjectVersion(this.projectId, entry.version).subscribe({
      next: () => {
        this.toastr.success(`Restored to version ${entry.version}`);
        this.restoring = false;
        this.loadHistory();
      },
      error: (err) => {
        this.toastr.error(err?.error?.error || 'Restore failed');
        this.restoring = false;
      },
    });
  }
}
